import React, { useMemo, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import {
  formatFileSize,
  isPdfFile,
  MAX_PDF_SIZE_BYTES,
  MAX_PDF_SIZE_MB,
} from "../utils/pdfHelpers";

const EmployeePdfAddModal = ({ show, onHide, onSave, saving }) => {
  const [pdfName, setPdfName] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [fileErrors, setFileErrors] = useState({});

  const hasOverSizeFile = useMemo(
    () => selectedFiles.some((file) => file.size > MAX_PDF_SIZE_BYTES),
    [selectedFiles]
  );

  const resetForm = () => {
    setPdfName("");
    setSelectedFiles([]);
    setFileErrors({});
  };

  const closeModal = () => {
    resetForm();
    onHide();
  };

  const addFiles = (fileList) => {
    const incomingFiles = Array.from(fileList || []);
    if (!incomingFiles.length) return;

    const invalidFile = incomingFiles.find((file) => !isPdfFile(file));
    if (invalidFile) {
      setFileErrors((prev) => ({
        ...prev,
        file: "Only PDF files are allowed.",
      }));
      return;
    }

    setSelectedFiles((prev) => {
      const nextFiles = [...prev];
      incomingFiles.forEach((file) => {
        const duplicate = nextFiles.some(
          (item) =>
            item.name === file.name &&
            item.size === file.size &&
            item.lastModified === file.lastModified
        );
        if (!duplicate) nextFiles.push(file);
      });
      return nextFiles;
    });
    setFileErrors((prev) => ({ ...prev, file: null }));
  };

  const handleFileChange = (event) => {
    addFiles(event.target.files);
    event.target.value = "";
  };

  const handleDrop = (event) => {
    event.preventDefault();
    addFiles(event.dataTransfer.files);
  };

  const removeFile = (index) => {
    setSelectedFiles((prev) =>
      prev.filter((_, fileIndex) => fileIndex !== index)
    );
    setFileErrors((prev) => ({ ...prev, file: null }));
  };

  const handleSubmit = async () => {
    const errors = {};

    if (!pdfName.trim()) errors.name = "PDF name is required.";
    if (!selectedFiles.length) {
      errors.file = "Please select at least one PDF file.";
    } else if (hasOverSizeFile) {
      errors.file = `Each file must be ${MAX_PDF_SIZE_MB} MB or less.`;
    }

    if (Object.keys(errors).length) {
      setFileErrors(errors);
      return;
    }

    const saved = await onSave({
      name: pdfName.trim(),
      files: selectedFiles,
    });

    if (saved) resetForm();
  };

  return (
    <Modal show={show} onHide={closeModal} centered>
      <Modal.Header
        closeButton
        style={{
          borderBottom: "0.5px solid var(--bs-border-color, #dee2e6)",
          padding: "1rem 1.25rem",
        }}
      >
        <div className="d-flex align-items-center gap-2">
          <div
            style={{
              width: 34,
              height: 34,
              borderRadius: 8,
              background: "#f85c63",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <i
              className="fas fa-file-upload"
              style={{ color: "white", fontSize: 16 }}
            />
          </div>
          <div>
            <Modal.Title
              style={{ fontSize: 15, fontWeight: 500, marginBottom: 1 }}
            >
              Add new PDF
            </Modal.Title>
            <div style={{ fontSize: 12, color: "#6c757d" }}>
              Upload one or more documents for employees
            </div>
          </div>
        </div>
      </Modal.Header>

      <Modal.Body
        style={{
          padding: "1.25rem",
          display: "flex",
          flexDirection: "column",
          gap: "1.1rem",
          maxHeight: "calc(100vh - 190px)",
          overflowY: "auto",
        }}
      >
        <div>
          <label
            className="form-label"
            style={{ fontSize: 12, fontWeight: 500, color: "#6c757d" }}
          >
            PDF name <span style={{ color: "#D85A30" }}>*</span>
          </label>
          <input
            type="text"
            maxLength={100}
            className={`form-control form-control-sm ${
              fileErrors.name ? "is-invalid" : ""
            }`}
            placeholder="e.g. Employee Handbook 2025"
            value={pdfName}
            onChange={(event) => {
              setPdfName(event.target.value);
              if (event.target.value.trim()) {
                setFileErrors((prev) => ({ ...prev, name: null }));
              }
            }}
          />
          <div className="d-flex justify-content-between align-items-center mt-1">
            {fileErrors.name ? (
              <div className="text-danger" style={{ fontSize: 11 }}>
                <i className="fas fa-exclamation-circle me-1" />
                {fileErrors.name}
              </div>
            ) : (
              <span />
            )}
            <span style={{ fontSize: 11, color: "#adb5bd" }}>
              {pdfName.length} / 100
            </span>
          </div>
        </div>

        <div>
          <label
            className="form-label"
            style={{ fontSize: 12, fontWeight: 500, color: "#6c757d" }}
          >
            PDF files <span style={{ color: "#D85A30" }}>*</span>
          </label>

          <label
            style={{
              display: "block",
              border: `1.5px dashed ${fileErrors.file ? "#E24B4A" : "#ced4da"}`,
              borderRadius: 8,
              padding: "1.5rem 1rem",
              textAlign: "center",
              cursor: "pointer",
              background: fileErrors.file ? "#FCEBEB" : "transparent",
              transition: "border-color .15s",
            }}
            onDragOver={(event) => event.preventDefault()}
            onDrop={handleDrop}
          >
            <input
              type="file"
              accept="application/pdf"
              multiple
              onChange={handleFileChange}
              style={{ display: "none" }}
            />
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                background: "#f8f9fa",
                border: "0.5px solid #dee2e6",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 10px",
              }}
            >
              <i
                className="fas fa-cloud-upload-alt"
                style={{ fontSize: 18, color: "#6c757d" }}
              />
            </div>
            <div style={{ fontSize: 13, color: "#6c757d" }}>
              Drag & drop or{" "}
              <span style={{ color: "#f85c63", fontWeight: 500 }}>
                browse files
              </span>
            </div>
            <div style={{ fontSize: 11, color: "#adb5bd", marginTop: 4 }}>
              PDF only · Max {MAX_PDF_SIZE_MB} MB each
            </div>
          </label>

          {fileErrors.file && (
            <div
              style={{
                fontSize: 11,
                color: "#E24B4A",
                marginTop: 4,
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <i className="fas fa-exclamation-circle" /> {fileErrors.file}
            </div>
          )}

          {selectedFiles.length > 0 && (
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 8,
                marginTop: 8,
                maxHeight: 248,
                overflowY: selectedFiles.length > 3 ? "auto" : "visible",
                paddingRight: selectedFiles.length > 3 ? 4 : 0,
              }}
            >
              {selectedFiles.map((file, index) => {
                const isOverSize = file.size > MAX_PDF_SIZE_BYTES;

                return (
                  <div
                    key={`${file.name}-${file.lastModified}-${index}`}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "10px 12px",
                      borderRadius: 8,
                      border: `0.5px solid ${
                        isOverSize ? "#F09595" : "#dee2e6"
                      }`,
                      background: isOverSize ? "#FCEBEB" : "#f8f9fa",
                    }}
                  >
                    <div
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: 6,
                        background: "white",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <i
                        className="fas fa-file-pdf"
                        style={{
                          fontSize: 15,
                          color:"#f85c63" ,
                        }}
                      />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 12,
                          fontWeight: 500,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          color: "#111827",
                        }}
                        title={file.name}
                      >
                        {file.name}
                      </div>
                      <div
                        style={{
                          fontSize: 11,
                          color: isOverSize ? "#E24B4A" : "#adb5bd",
                        }}
                      >
                        {formatFileSize(file.size)}
                        {isOverSize
                          ? ` · exceeds ${MAX_PDF_SIZE_MB} MB limit`
                          : ""}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      style={{
                        width: 24,
                        height: 24,
                        borderRadius: 6,
                        border: "none",
                        background: "none",
                        cursor: "pointer",
                        color: "#adb5bd",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                      aria-label={`Remove ${file.name}`}
                    >
                      <i className="fas fa-times" style={{ fontSize: 12 }} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer
        style={{
          borderTop: "0.5px solid var(--bs-border-color, #dee2e6)",
          padding: "1rem 1.25rem",
        }}
      >
        <Button variant="light" onClick={closeModal} style={{ fontSize: 13 }}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={saving}
          style={{
            background: "#f85c63",
            border: "none",
            fontSize: 13,
            fontWeight: 500,
            display: "flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          <i className="fas fa-upload" />
          {saving ? "Saving..." : "Save PDFs"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default EmployeePdfAddModal;
